"use client";
import React, { useState, useRef, useEffect, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CategoryContext } from "../../context/CategoryContext";

const FilterSidebar = ({ filters, onFiltersChange }) => {
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(null);
  const sliderRef = useRef(null);
  const { categories, loading } = useContext(CategoryContext);

  const sortOptions = ["Free", "Premium"];

  // ✅ Scroll to top whenever filters panel opens or filters change
  useEffect(() => {
    if (isFiltersOpen) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [isFiltersOpen]);

  const toggleFilters = () => setIsFiltersOpen(!isFiltersOpen);
  const ageRangeRef = useRef(filters.ageRange);

  useEffect(() => {
    ageRangeRef.current = filters.ageRange;
  }, [filters.ageRange]);

  const handleStart = (index) => (e) => {
    e.preventDefault();
    setIsDragging(index);
  };

  const handleMove = (e) => {
    if (isDragging === null || !sliderRef.current) return;

    const rect = sliderRef.current.getBoundingClientRect();
    const clientX = e.type.includes("mouse") ? e.clientX : e.touches[0].clientX;

    let percentage = (clientX - rect.left) / rect.width;
    percentage = Math.max(0, Math.min(1, percentage)); // clamp 0-1
    const newValue = Math.round(percentage * 20);

    const newRange = [...ageRangeRef.current];

    if (isDragging === 0) {
      newRange[0] = Math.min(newValue, newRange[1]);
    } else {
      newRange[1] = Math.max(newValue, newRange[0]);
    }

    handleFilterChange("ageRange", newRange);
  };

  const handleEnd = () => setIsDragging(null);

  useEffect(() => {
    if (isDragging !== null) {
      document.addEventListener("mousemove", handleMove);
      document.addEventListener("mouseup", handleEnd);
      document.addEventListener("touchmove", handleMove);
      document.addEventListener("touchend", handleEnd);

      return () => {
        document.removeEventListener("mousemove", handleMove);
        document.removeEventListener("mouseup", handleEnd);
        document.removeEventListener("touchmove", handleMove);
        document.removeEventListener("touchend", handleEnd);
      };
    }
  }, [isDragging]);

  // Update filters immutably
  const handleFilterChange = (key, value) => {
    onFiltersChange({ ...filters, [key]: value });

    // ✅ Scroll to top on any filter change
    // window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Reset all filters
  const resetFilters = () => {
    onFiltersChange({
      toyTypes: categories.reduce(
        (acc, cat) => ({ ...acc, [cat.name]: false }),
        {}
      ),
      ageRange: [0, 20],
      sortBy: { Free: false, Premium: false },
    });

    // ✅ Scroll to top after reset
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Slider helpers
  const getPercentage = (val) => (val / 20) * 100;

  const CheckboxItem = ({ label, checked, onChange, showBorder }) => (
    <div
      className={`flex justify-between items-center p-2.5 cursor-pointer hover:bg-gray-50 ${
        showBorder ? "border-b border-gray-300" : ""
      }`}
    >
      <label className="text-sm text-neutral-600 flex-1 cursor-pointer">{label}</label>
      <div
        className={`w-4 h-4 rounded cursor-pointer transition-colors duration-200 ${
          checked ? "bg-sky-950" : "bg-zinc-100"
        }`}
        onClick={() => onChange(!checked)}
        role="checkbox"
        aria-checked={checked}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onChange(!checked);
          }
        }}
      >
        {checked && (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M4 6.667L2.667 8L6.667 12L13.334 5.333L12 4L6.667 9.333L4 6.667Z"
              fill="white"
            />
          </svg>
        )}
      </div>
    </div>
  );

  const FilterSection = ({ title, children }) => (
    <section className="flex flex-col bg-white rounded-lg shadow-lg w-full max-xl:flex-1 max-xl:min-w-[250px] max-sm:min-w-[auto]">
      <header className="flex items-center px-5 py-2.5 h-10">
        <h2 className="text-sm font-bold text-sky-950">{title}</h2>
      </header>
      <div className="flex flex-col pb-2.5 mx-auto w-[255px] max-xl:w-full">
        {children}
      </div>
    </section>
  );

  const ChevronIcon = ({ isOpen }) => (
    <motion.svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      animate={{ rotate: isOpen ? 180 : 0 }}
      transition={{ duration: 0.3 }}
      aria-hidden="true"
    >
      <path
        d="M6 9L12 15L18 9"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </motion.svg>
  );

  return (
    <aside className="flex flex-col shrink-0 gap-7 w-[285px] max-xl:flex-row max-xl:flex-wrap max-xl:w-full max-sm:flex-col">
      {/* Header */}
      <header className="flex justify-between items-center w-full">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-bold text-neutral-600">Filter</h1>
          <button
            onClick={toggleFilters}
            className="xl:hidden flex items-center justify-center p-1 text-neutral-600 hover:text-sky-950 rounded focus:outline-none focus:ring-2 focus:ring-sky-950 focus:ring-offset-2"
            aria-label={isFiltersOpen ? "Close filters" : "Open filters"}
          >
            <ChevronIcon isOpen={isFiltersOpen} />
          </button>
        </div>
        <button
          onClick={resetFilters}
          className={`text-base font-bold text-red-500 cursor-pointer hover:text-red-600 rounded focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 ${
            !isFiltersOpen ? "xl:block max-xl:hidden" : "block"
          }`}
          aria-label="Reset all filters"
        >
          Reset
        </button>
      </header>

      {/* Filter Sections */}
      <div className="xl:flex xl:flex-col xl:gap-7 max-xl:w-full">
        <AnimatePresence>
          {(isFiltersOpen ||
            typeof window === "undefined" ||
            window.innerWidth >= 1280) && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="xl:contents max-xl:overflow-hidden max-xl:flex max-xl:flex-col max-xl:gap-7"
            >
              {/* Toy Type */}
              <FilterSection title="Toy Type">
                {loading ? (
                  <div className="p-4 text-sm text-neutral-500">
                    Loading categories...
                  </div>
                ) : (
                  categories.map((cat, i) => (
                    <CheckboxItem
                      key={cat.id}
                      label={cat.name}
                      checked={filters.toyTypes[cat.name] || false}
                      onChange={(checked) =>
                        handleFilterChange("toyTypes", {
                          ...filters.toyTypes,
                          [cat.name]: checked,
                        })
                      }
                      showBorder={i < categories.length - 1}
                    />
                  ))
                )}
              </FilterSection>

              {/* Age Range */}
              <FilterSection title="Age Range">
                <div className="px-2.5 pt-0 pb-5 w-full relative">
                  <div className="relative mt-8 h-[50px]" ref={sliderRef}>
                    {/* Min/Max Labels */}
                    <div className="absolute left-0 text-xs text-neutral-500 -bottom-5">
                      0
                    </div>
                    <div className="absolute right-0 text-xs text-neutral-500 -bottom-5">
                      20
                    </div>

                    {/* Slider Track */}
                    <div className="absolute w-full h-1.5 rounded bg-stone-300 bg-opacity-30 top-[25px]" />

                    {/* Active Range */}
                    <div
                      className="absolute h-1.5 rounded bg-sky-950 top-[25px]"
                      style={{
                        left: `${(filters.ageRange[0] / 20) * 100}%`,
                        width: `${((filters.ageRange[1] - filters.ageRange[0]) / 20) * 100}%`,
                      }}
                    />

                    {/* Slider Handles */}
                    {filters.ageRange.map((val, i) => (
                      <div
                        key={i}
                        className="absolute top-0 h-[50px] w-[20px] cursor-pointer touch-none flex flex-col items-center"
                        style={{ left: `calc(${(val / 20) * 100}% - 10px)` }}
                        onMouseDown={handleStart(i)}
                        onTouchStart={handleStart(i)}
                        role="slider"
                        aria-valuemin={0}
                        aria-valuemax={20}
                        aria-valuenow={val}
                        aria-label={`${i === 0 ? 'Minimum' : 'Maximum'} age: ${val} years`}
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
                            e.preventDefault();
                            const newRange = [...filters.ageRange];
                            if (i === 0) {
                              newRange[0] = Math.max(0, newRange[0] - 1);
                            } else {
                              newRange[1] = Math.max(newRange[0], newRange[1] - 1);
                            }
                            handleFilterChange("ageRange", newRange);
                          } else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
                            e.preventDefault();
                            const newRange = [...filters.ageRange];
                            if (i === 0) {
                              newRange[0] = Math.min(newRange[1], newRange[0] + 1);
                            } else {
                              newRange[1] = Math.min(20, newRange[1] + 1);
                            }
                            handleFilterChange("ageRange", newRange);
                          }
                        }}
                      >
                        {/* Value Label */}
                        <div className="text-xs text-center text-black font-medium mb-1">
                          {val}
                        </div>

                        {/* Circular Handle */}
                        <div
                          className={`w-5 h-5 rounded-full border-2 border-sky-950 bg-white shadow-md transition-all duration-200 ${
                            isDragging === i
                              ? 'scale-110 shadow-lg border-sky-700'
                              : 'hover:scale-105 hover:shadow-lg hover:border-sky-700'
                          }`}
                        >
                          {/* Inner dot for better visibility */}
                          <div className="w-full h-full rounded-full bg-sky-950 scale-50 transition-transform duration-200" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </FilterSection>

              {/* Sort By - Commented out as per original */}
              {/* <FilterSection title="Sort By">
                {sortOptions.map((option) => (
                  <CheckboxItem
                    key={option}
                    label={option}
                    checked={filters.sortBy[option] || false}
                    onChange={() => {
                      const isCurrentlySelected = filters.sortBy[option];
                      handleFilterChange("sortBy", {
                        Free: option === "Free" ? !isCurrentlySelected : false,
                        Premium:
                          option === "Premium" ? !isCurrentlySelected : false,
                      });
                    }}
                  />
                ))}
              </FilterSection> */}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </aside>
  );
};

export default FilterSidebar;
